import React from 'react'
import cx from 'classnames'
import zip from 'lodash/zip'
import flatten from 'lodash/flatten'
import RA from 'ra-config'
import I18n from 'lib/components/i18n'
import comparisonGroups from './comparison-groups'
import InfoLabel from '../../info-label'
import { getShades } from '../../../config/colors'
import { labelForFactor } from '../../../attributes'
import { descriptionForFactor } from '../../../config/description'
import * as placeholder from 'lib/config/placeholder'
import { formatTable } from '../../../format'

const TOOLTIPS = RA.content.factors.attributeInfoText
const TABLE_BODY = '.performance-table__body'

const pluckKey = cell => `${cell.ID}-${cell.Name}`

function Headings({ cells, source }) {
  const shades = getShades(cells, pluckKey)

  const children = cells.map(factor => {
    const key = pluckKey(factor)
    const style = { borderBottom: '4px solid ' + shades[key] }

    return (
      <th key={key} style={style} className="performance-table__header">
        {factor.Label}
      </th>
    )
  })

  const sourceText = source ? placeholder.replace('{sourceText}') : ''

  return (
    <thead>
      <tr>
        <th className="source-text">
          <span hidden>Factor</span>
          <span>{sourceText}</span>
        </th>
        {children}
      </tr>
    </thead>
  )
}

function SingleRow({ definition, cells }) {
  const { attribute } = definition
  const label = labelForFactor(attribute)

  return (
    <tr key={attribute}>
      <td key="label" className="performance-table__row-label">
        <InfoLabel
          overlay={TOOLTIPS[attribute]}
          label={label}
          scrollParent={TABLE_BODY}
        />
      </td>
      {cells.map(factor => {
        const value = factor[attribute]

        return (
          <td key={attribute + factor.ID}>{formatTable(value, attribute)}</td>
        )
      })}
    </tr>
  )
}

function SplitRow({ definition, cells }) {
  const { labelAttribute, attributePrefix, attributeSuffixes } = definition
  const label = labelForFactor(labelAttribute)

  return (
    <tr className="performance-table__row-split">
      <td className="performance-table__row-label">
        <InfoLabel
          overlay={TOOLTIPS[labelAttribute]}
          label={label}
          scrollParent={TABLE_BODY}
        />
      </td>
      {cells.map(factor => {
        const values = attributeSuffixes.map(suffix => {
          let attribute = attributePrefix + suffix
          return [attribute, factor[attribute]]
        })

        return (
          <td
            key={attributePrefix + factor.ID}
            className="performance-table__row-split__cell"
          >
            <table>
              <tbody>
                <tr>
                  {values.map(([attribute, value]) => {
                    return (
                      <td key={attribute}>{formatTable(value, attribute)}</td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </td>
        )
      })}
    </tr>
  )
}

function TableRow({ definition, cells }) {
  const { labelAttribute, headings, attributes } = definition
  const label = labelForFactor(labelAttribute)

  return (
    <tr>
      <td className="performance-table__row-label">{label}</td>
      {cells.map(factor => {
        const values = attributes.map(attribute => [
          attribute,
          factor[attribute]
        ])

        return (
          <td
            key={factor.ID}
            className="performance-table__header-nested--condensed"
          >
            <table className="performance-table__nested-table">
              <thead>
                <tr>
                  {headings.map((heading, i) => (
                    <th key={i} dangerouslySetInnerHTML={{ __html: heading }} />
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {values.map(([attribute, value], i) => {
                    return <td key={i}>{formatTable(value, attribute)}</td>
                  })}
                </tr>
              </tbody>
            </table>
          </td>
        )
      })}
    </tr>
  )
}

function Row(props) {
  if (props.definition.type === 'single') return <SingleRow {...props} />
  if (props.definition.type === 'split') return <SplitRow {...props} />
  if (props.definition.type === 'table') return <TableRow {...props} />
}

function WysiwygCell({ text, classes }) {
  return (
    <td className={classes}>
      <span
        className="wysiwyg"
        dangerouslySetInnerHTML={{ __html: text || 'No description' }}
      />
    </td>
  )
}

function Description({ cells, region }) {
  return (
    <tbody>
      <tr className="performance-table__row-align-top">
        <td className="performance-table__row-label">Description</td>
        {cells.map(factor => {
          const text = descriptionForFactor(
            factor.Name,
            factor.Category,
            region.value
          )
          return (
            <WysiwygCell
              key={pluckKey(factor)}
              text={text}
              classes="performance-table__description"
            />
          )
        })}
      </tr>
    </tbody>
  )
}

function StatsGroup({ group, cells }) {
  const { label, showInnerColumnHeadings, attributes } = group
  const tableClass = cx('table-body-bordered-bottom', {
    'table-body-empty': !showInnerColumnHeadings
  })

  return (
    <tbody>
      <tr className="performance-table__row-split performance-table__row-split--condensed">
        <I18n tag="td" className="performance-table__row-group-label">
          {label}
        </I18n>

        {cells.map((factor, i) => {
          return (
            <th key={pluckKey(factor)}>
              <table className={tableClass}>
                <tbody>
                  <tr className="performance-table__inner-column-headings">
                    <th>{showInnerColumnHeadings && 'Net of Trading Cost'}</th>
                    <th>
                      {showInnerColumnHeadings && 'Gross of Trading Cost'}
                    </th>
                  </tr>
                </tbody>
              </table>
            </th>
          )
        })}
      </tr>
      {attributes.map((definition, i) => {
        return <Row key={label + i} definition={definition} cells={cells} />
      })}
    </tbody>
  )
}

export default function HistoricalTable({ cells = [], region }) {
  const groups = comparisonGroups.map(group => {
    return <StatsGroup key={group.label} group={group} cells={cells} />
  })

  const headings = new Array(comparisonGroups.length - 1)
    .fill(null)
    .map((_, i) => <Headings key={i} cells={cells} />)

  const components = flatten(zip(groups, headings))

  return (
    <table>
      <Headings cells={cells} source={true} />
      <Description cells={cells} region={region} />
      {components}
    </table>
  )
}



// WEBPACK FOOTER //
// ./src/components/factors/historical/table.js